/**
 * The purchase course pages.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { rpx } from 'client/lib/rpx-client';
import { useCurrentTenant, useCurrentUser } from 'client/lib/auth';
import {
  CourseCheckoutErrorPage,
  CourseCheckoutPage,
  finalCheckoutPageURL,
} from '@course/components/course-checkout';
import * as fmt from 'shared/payments/fmt';
import { useIntl } from 'shared/intl/use-intl';
import * as pmtmath from 'shared/payments/math';
import { PurchaseForm, CombinedCheckoutForm } from '@course/components/checkout';
import { setUrlSearchParam } from 'client/utils/url';
import { GiftForm, useGiftState } from './gift-form';
import * as terminology from 'shared/terminology';
import { ErrorPage } from '@components/error-page';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { PurchaseFn, StripeHandlerFn } from 'shared/payments/types';
import { isNullish } from 'shared/utils';
import { showUpsellCheckoutModal } from '../guide-upsells/checkout-modal';
import { defStudentRoute } from '@course/components/courses-app-router';
import { getCourseNoSeatsAvailableMessage } from 'shared/messages';
import { ReadonlyMinidoc } from '@components/minidoc/readonly-minidoc';
import { CustomCheckoutPage } from '@course/components/sales-checkout/custom-checkout-page';
import { HtmlTemplateCheckoutPage } from '@course/components/sales-checkout/html-template-checkout-page';
import { MiraseeCheckoutTemplate } from '@course/components/sales-checkout/mirasee-checkout-template';
import { isHtmlTemplate, isMiraseeCheckout, isSectionArray } from 'server/types/pmts-overrides';

async function load(route: RouteLoadProps) {
  const { courseId, priceId, couponId } = route.params;
  const result = await rpx.courseCheckout.getCourseCheckoutState({ courseId, priceId, couponId });
  return result;
}

type Props = LoadedProps<typeof load>;
type Data = Props['state'];

export const route = defStudentRoute({
  isPublic: true,
  load,
  Page,
});

function RepurchasingForm({
  course,
  onAllowRepurchase,
}: {
  course: Data['course'];
  onAllowRepurchase(): void;
}) {
  const tenant = useCurrentTenant();
  const intl = useIntl();
  const courseLabel = terminology.courseLabel({
    course,
    intl,
    tenant,
  });
  return (
    <div class="flex flex-col justify-center gap-2">
      <p class="text-gray-500">
        {intl("Important: You're already enrolled in this {courseLabel:string}.", {
          courseLabel,
        })}
      </p>
      <BtnPrimary class="my-2" onClick={onAllowRepurchase}>
        {intl('Continue with signup')}
      </BtnPrimary>
      <BtnSecondary
        href={finalCheckoutPageURL({
          id: course.id,
          title: course.title,
        })}
      >
        {intl('Go to {courseLabel:string}', {
          courseLabel,
        })}
      </BtnSecondary>
    </div>
  );
}

function removeNullProps<T extends Record<string, any>>(obj: T) {
  const result = { ...obj };
  for (const k in result) {
    if (isNullish(result[k])) {
      delete result[k];
    }
  }
  return result;
}

function Page(props: Props) {
  const user = useCurrentUser();
  const intl = useIntl();
  const { course, price, coupon, seller } = props.state;
  const [allowRepurchase, setAllowRepurchase] = useState(false);
  const [giftState, setGiftState] = useGiftState(course);
  const isGivingGift = giftState?.email;
  const isFree = !!price && pmtmath.isFree({ price, coupon });
  const cartItems = useRef<undefined | string[]>();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const affiliateKeys = ['orid', 'opid', 'aff', 'ref', 'utm_source'];
    const tracking: Record<string, string> = {};
    for (const key of affiliateKeys) {
      const val = params.get(key);
      if (val) {
        tracking[key] = val;
      }
    }
    if (Object.keys(tracking).length > 0) {
      sessionStorage.setItem('affiliate_tracking', JSON.stringify(tracking));
    }
  }, []);

  if (!course || !price) {
    return <ErrorPage title={intl('404 | Not Found')}></ErrorPage>;
  }

  const setCoupon = (value: typeof coupon) => {
    if (value) {
      props.router.rewrite(setUrlSearchParam('couponId', value.id));
    }
    props.setState((s) => ({ ...s, coupon: value }));
  };

  const purchaseCourse: PurchaseFn = async (opts) => {
    const affiliateRaw = sessionStorage.getItem('affiliate_tracking');
    const affiliateParsed = affiliateRaw ? JSON.parse(affiliateRaw) : undefined;
    const affiliateCode = affiliateParsed?.aff || affiliateParsed?.orid;

    if (
      giftState?.email ||
      !price.upsellId ||
      opts.priceId === price.id ||
      !opts.stripePaymentMethod
    ) {
      return rpx.courseCheckout.purchase({
        ...opts,
        courseId: course.id,
        courseTitle: course.title,
        priceId: price.id,
        couponId: coupon?.id,
        giftRecipientEmail: giftState?.email,
        address: opts?.address ? removeNullProps(opts.address) : undefined,
        affiliateCode,
      });
    }
    // Purchase an upsell offer.
    return await rpx.courseCheckout.purchaseUpsell({
      address: opts.address,
      priceId: opts.priceId!,
      basePriceId: price.id,
      stripePaymentMethod: opts.stripePaymentMethod!,
      affiliateCode,
    });
  };

  const stripeHandler: StripeHandlerFn = async (opts) => {
    const isGift = !!giftState?.email;
    const basicCheckout = () => opts.checkout({ priceId: price.id, couponId: coupon?.id });
    if (isGift || !price.upsellId) {
      return basicCheckout();
    }
    const upsell = await rpx.upsells.getStudentUpsell({ appliedPriceId: price.id });
    if (!upsell) {
      return basicCheckout();
    }
    const result = await showUpsellCheckoutModal({
      upsell,
      course,
      price,
      coupon,
      cartItems: cartItems.current,
      async purchase(upsellPrice, upsellCoupon) {
        return opts.checkout({
          priceId: upsellPrice.id,
          couponId: upsellCoupon?.id,
        });
      },
    });
    cartItems.current = result?.cartItems;
    return result?.ok;
  };

  const onPurchaseComplete = (opts?: { newUser?: boolean }) =>
    props.router.redirectTo(
      `/courses/${course.id}/provision?gift=${!!giftState?.email}${opts?.newUser ? '&newuser=true' : ''}`,
    );

  const giftForm = course.isProduct ? null : (
    <GiftForm
      course={course}
      price={price}
      state={giftState}
      setState={setGiftState}
      previousGifts={props.state.previousGiftEmails}
    />
  );
  if (course.status !== 'published') {
    return (
      <CourseCheckoutErrorPage course={course} title={intl('Signups closed')}>
        {intl('This course is not currently accepting signups.')}
      </CourseCheckoutErrorPage>
    );
  }

  if (props.state.isFull) {
    return (
      <CourseCheckoutErrorPage course={course} title={intl('This course is full.')} noContactGuide>
        <ReadonlyMinidoc
          content={
            props.state.course.noSeatsAvailableMessage ||
            getCourseNoSeatsAvailableMessage(intl, course.guide.email)
          }
        />
      </CourseCheckoutErrorPage>
    );
  }

  if (price.availability !== 'available') {
    return (
      <CourseCheckoutErrorPage
        course={course}
        title={
          price.availability === 'notstarted'
            ? intl('This price is not available yet.')
            : intl('This price is no longer available.')
        }
      >
        {course.guide &&
          fmt.availability({
            item: price,
            intl,
          })}
      </CourseCheckoutErrorPage>
    );
  }

  if (giftState?.isOpen) {
    return (
      <CourseCheckoutPage course={course} price={price} coupon={coupon} setCoupon={setCoupon}>
        {giftForm}
      </CourseCheckoutPage>
    );
  }

  if (course.useCustomCheckout && price.checkoutPageConfig && !isGivingGift) {
    if (isMiraseeCheckout(price.checkoutPageConfig)) {
      return (
        <MiraseeCheckoutTemplate
          config={price.checkoutPageConfig}
          course={course}
          price={price}
          coupon={coupon}
          collectAddress={seller.collectAddress}
          handleStripe={stripeHandler}
          beginPurchase={purchaseCourse}
          onPurchaseComplete={onPurchaseComplete}
        />
      );
    }
    if (isHtmlTemplate(price.checkoutPageConfig)) {
      return (
        <HtmlTemplateCheckoutPage
          config={price.checkoutPageConfig}
          course={course}
          price={price}
          coupon={coupon}
          collectAddress={seller.collectAddress}
          handleStripe={stripeHandler}
          beginPurchase={purchaseCourse}
          onPurchaseComplete={onPurchaseComplete}
        />
      );
    }
    if (isSectionArray(price.checkoutPageConfig) && price.checkoutPageConfig.length) {
      return (
        <CustomCheckoutPage
          config={price.checkoutPageConfig}
          course={course}
          price={price}
          coupon={coupon}
          collectAddress={seller.collectAddress}
          handleStripe={stripeHandler}
          beginPurchase={purchaseCourse}
          onPurchaseComplete={onPurchaseComplete}
        />
      );
    }
  }

  if (!user) {
    return (
      <CourseCheckoutPage
        course={course}
        price={price}
        coupon={coupon}
        setCoupon={setCoupon}
        showHeader={false}
      >
        {price.checkoutContent && (
          <div class="prose max-w-none mb-6">
            <ReadonlyMinidoc content={price.checkoutContent} />
          </div>
        )}
        <CombinedCheckoutForm
          price={price}
          coupon={coupon}
          collectAddress={seller.collectAddress}
          isGift={!!giftState?.email}
          giftingToEmail={giftState?.email}
          handleStripe={stripeHandler}
          beginPurchase={purchaseCourse}
          onPurchaseComplete={onPurchaseComplete}
          subtitle={price.name}
          footer={price.paymentType === 'paid' && giftForm}
        />
      </CourseCheckoutPage>
    );
  }

  if (!isGivingGift && !allowRepurchase && props.state.isEnrolledToCourse) {
    return (
      <CourseCheckoutPage course={course} price={price} coupon={coupon} setCoupon={setCoupon}>
        <RepurchasingForm course={course} onAllowRepurchase={() => setAllowRepurchase(true)} />
        {giftForm}
      </CourseCheckoutPage>
    );
  }

  return (
    <CourseCheckoutPage
      course={course}
      price={price}
      coupon={coupon}
      setCoupon={setCoupon}
      title={isFree ? intl('Enroll Now') : intl('Payment')}
    >
      {price.checkoutContent && (
        <div class="prose max-w-none mb-6">
          <ReadonlyMinidoc content={price.checkoutContent} />
        </div>
      )}
      <PurchaseForm
        collectAddress={seller.collectAddress}
        autoSubmit={props.params.auto === 'true'}
        price={price}
        coupon={coupon}
        handleStripe={stripeHandler}
        beginPurchase={purchaseCourse}
        onPurchaseComplete={onPurchaseComplete}
        isGift={!!giftState?.email}
        footer={price.paymentType === 'paid' && giftForm}
      />
    </CourseCheckoutPage>
  );
}
